var _a;
import { __assign, __read } from "tslib";
import React, { useState, useMemo, useRef, useContext, useEffect } from 'react';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import Marks from '../marks';
import Step from '../steps/';
import { groupMarksByPosition, findMarksInCluster, showAllMarks, } from '../../utils';
import { HoverBar, hoverBarMotion } from '../../../../components';
import { ChartContext } from '../../../../contexts';
import { AnimationVariants } from './types';
import GradientFilter from '../gradient-filter';
import ClipPath from '../clip-path';
var HOVER_BAR_HIDE_TIME = 300;
var createLineMotion = function (color, isActive) {
    var _a;
    return (_a = {},
        _a[AnimationVariants.Hidden] = {
            pathLength: 0,
            stroke: 'rgba(0, 0, 0, 0)',
            opacity: 1,
            transition: { duration: 0, delay: 0 },
        },
        _a[AnimationVariants.Visible] = {
            pathLength: 1,
            stroke: color,
            opacity: 1,
            transition: { duration: 0.3, delay: 0.2 },
        },
        _a[AnimationVariants.Active] = {
            pathLength: 1,
            stroke: color,
            opacity: isActive ? 1 : 0.2,
            transition: isActive ? { delay: 0.2, duration: 0.3 } : { duration: 0.3 },
        },
        _a[AnimationVariants.OffsetChange] = {
            pathLength: 1,
            stroke: color,
            opacity: 1,
            transition: { delay: 0, duration: 0.5 },
        },
        _a);
};
var createAreaMotion = (_a = {},
    _a[AnimationVariants.Hidden] = {
        opacity: 0,
        transition: { duration: 0, delay: 0 },
    },
    _a[AnimationVariants.Visible] = {
        opacity: 1,
        transition: { delay: 0.7, duration: 0.8 },
    },
    _a[AnimationVariants.OffsetChange] = {
        opacity: 1,
        transition: { delay: 0, duration: 0.5 },
    },
    _a);
var Lines = function (_a) {
    var lines = _a.lines, marks = _a.marks, steps = _a.steps, areas = _a.areas, colorPalette = _a.colorPalette, curve = _a.curve, activeKey = _a.activeKey, stackMode = _a.stackMode, areaMode = _a.areaMode, groupMode = _a.groupMode, stepMode = _a.stepMode, gradient = _a.gradient, gradientBlocks = _a.gradientBlocks, onMarkMouseEnter = _a.onMarkMouseEnter, onMarkMouseLeave = _a.onMarkMouseLeave, dataSeriesOffset = _a.dataSeriesOffset;
    var _b = __read(useState(false), 2), initialDrawFinished = _b[0], setInitialDraw = _b[1];
    var lineControls = useAnimation();
    var areaControls = useAnimation();
    var hideHoverBar = useRef(null);
    var _c = __read(useState({
        visible: false,
        x: 0,
    }), 2), hoverBarState = _c[0], setHoverBar = _c[1];
    var _d = useContext(ChartContext), height = _d.svgDimensions.height, hoverBar = _d.theme.hoverBar;
    useEffect(function () {
        if (initialDrawFinished) {
            setInitialDraw(false);
        }
        lineControls
            .start(AnimationVariants.Visible, { delay: 0.5, duration: 0.8 })
            .then(function () {
            setInitialDraw(true);
        });
        if (areaMode) {
            areaControls.start(AnimationVariants.Visible, {
                delay: 0.5,
                duration: 0.8,
            });
        }
    }, [
        curve,
        groupMode,
        stackMode,
        stepMode,
        lines.length,
        colorPalette,
        areaMode,
    ]);
    useEffect(function () {
        if (activeKey) {
            lineControls.start(AnimationVariants.Active).then(function () {
                setInitialDraw(true);
            });
        }
        else if (initialDrawFinished) {
            lineControls.start(AnimationVariants.Visible, {
                delay: 0.4,
                duration: 0.3,
            });
        }
    }, [activeKey]);
    useEffect(function () {
        if (initialDrawFinished) {
            lineControls.start(AnimationVariants.OffsetChange);
            if (areaMode) {
                areaControls.start(AnimationVariants.OffsetChange);
            }
        }
    }, [dataSeriesOffset, areaMode]);
    var groupedMarks = useMemo(function () { return groupMarksByPosition(marks); }, [marks]);
    var allMarks = showAllMarks(stepMode, marks, lines);
    return (React.createElement(React.Fragment, null,
        lines.map(function (_a, idx) {
            var key = _a.key, d = _a.d, color = _a.color, strokeWidth = _a.strokeWidth;
            return (React.createElement("g", { key: key },
                React.createElement(motion.path, { key: key + "-" + curve + "-" + stackMode + "-" + groupMode + "-" + (areaMode ? 'area' : 'line'), d: d, variants: createLineMotion(color, key === activeKey), animate: lineControls, stroke: color, strokeWidth: strokeWidth, initial: AnimationVariants.Hidden, fill: "transparent" }),
                areas.length && gradientBlocks.length && (React.createElement(React.Fragment, null,
                    gradient && (React.createElement(GradientFilter, { filterId: "gradient-" + areas[idx].id, positiveColor: areas[idx].positiveColor, zeroPointColor: areas[idx].zeroPointColor, negativeColor: areas[idx].negativeColor, gradientZeroPercent: areas[idx].gradientZeroPercent })),
                    React.createElement(ClipPath, { id: "clip-" + areas[idx].id, area: areas[idx].d }),
                    React.createElement(motion.rect, { x: gradientBlocks[idx].x, y: gradientBlocks[idx].y, width: gradientBlocks[idx].width, height: gradientBlocks[idx].height, fill: gradient ? "url(#gradient-" + areas[idx].id + ")" : color, clipPath: "url(#clip-" + areas[idx].id + ")", key: key + "-" + curve + "-" + stackMode + "-" + groupMode + "-" + color + "-area-gradient", variants: createAreaMotion, initial: AnimationVariants.Hidden, animate: areaControls })))));
        }),
        React.createElement(AnimatePresence, null, hoverBar.enabled && hoverBarState.visible && (React.createElement(motion.g, __assign({}, hoverBarMotion),
            React.createElement(HoverBar, { onMouseEnter: function () {
                    return hideHoverBar.current && clearTimeout(hideHoverBar.current);
                }, onMouseLeave: function () {
                    hideHoverBar.current = setTimeout(function () {
                        setHoverBar({
                            visible: false,
                            x: 0,
                        });
                    }, HOVER_BAR_HIDE_TIME);
                }, x: hoverBarState.x })))),
        allMarks ? (React.createElement(Step, { steps: steps, marks: groupedMarks, onMouseEnter: function (e, mark) {
                if (hideHoverBar.current)
                    clearTimeout(hideHoverBar.current);
                onMarkMouseEnter(e, findMarksInCluster(mark, groupedMarks, mark.height));
                setHoverBar({ x: mark.middle + mark.width / 2, visible: true });
            }, onMouseMove: function (e, mark) {
                if (hideHoverBar.current)
                    clearTimeout(hideHoverBar.current);
                onMarkMouseEnter(e, findMarksInCluster(mark, groupedMarks, mark.height));
                setHoverBar({ x: mark.middle + mark.width / 2, visible: true });
            }, onMouseLeave: function (e) {
                onMarkMouseLeave(e);
                hideHoverBar.current = setTimeout(function () {
                    setHoverBar({
                        visible: false,
                        x: 0,
                    });
                }, HOVER_BAR_HIDE_TIME);
            } })) : (React.createElement(Marks, { marks: marks, curve: curve, colorPalette: colorPalette, groupMode: groupMode, stackMode: stackMode, steps: steps, activeKey: activeKey, onMouseEnter: function (e, mark) {
                if (hideHoverBar.current)
                    clearTimeout(hideHoverBar.current);
                var marksRange;
                if (groupMode === 'stacked')
                    marksRange = height;
                onMarkMouseEnter(e, findMarksInCluster(mark, groupedMarks, marksRange).map(function (_a) {
                    var selector = _a.selector, color = _a.color;
                    return ({
                        selector: selector,
                        color: color,
                    });
                }));
                setHoverBar({ x: mark.x, visible: true });
            }, onMouseLeave: function (e) {
                onMarkMouseLeave(e);
                hideHoverBar.current = setTimeout(function () {
                    setHoverBar({
                        visible: false,
                        x: 0,
                    });
                }, HOVER_BAR_HIDE_TIME);
            } }))));
};
export default Lines;
